import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { CODE_LENGTH, formatCode, normalizeCode, parsePairingInput } from '../lib/code'

/**
 * OTP-style 6-digit input shown as `482 731`. A single real <input> sits invisibly on top of the
 * boxes, so typing, deleting, mobile numeric keyboards, autofill and paste all behave natively.
 * Pasting a full QR URL (`…/pair?c=482731&k=…`) also yields the claim nonce.
 */
export function CodeInput({
  value,
  onChange,
  onNonce,
  invalid,
  disabled,
  autoFocus,
  id,
  'aria-describedby': describedBy,
}: {
  value: string
  onChange: (code: string) => void
  onNonce?: (nonce: string | null) => void
  invalid?: boolean
  disabled?: boolean
  autoFocus?: boolean
  id?: string
  'aria-describedby'?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  const [focused, setFocused] = useState(false)
  const digits = normalizeCode(value)
  const activeIndex = Math.min(digits.length, CODE_LENGTH - 1)

  return (
    <div className="relative mx-auto w-fit" onClick={() => ref.current?.focus()}>
      <div className={cn('flex items-center gap-2 sm:gap-2.5', disabled && 'opacity-60')} aria-hidden>
        {Array.from({ length: CODE_LENGTH }, (_, i) => {
          const char = digits[i]
          const active = focused && i === activeIndex && digits.length < CODE_LENGTH
          return (
            <div key={i} className="flex items-center">
              {i === 3 && <span className="mr-2 h-0.5 w-3 rounded-full bg-border sm:mr-2.5" />}
              <div
                className={cn(
                  'relative flex h-14 w-11 items-center justify-center rounded-xl border bg-card font-mono text-2xl font-semibold shadow-xs transition-all sm:h-16 sm:w-12 sm:text-[28px]',
                  char && 'border-primary/40',
                  active && 'border-primary ring-4 ring-primary/15',
                  invalid && 'border-destructive ring-destructive/15',
                )}
              >
                {char ?? ''}
                {active && !char && <span className="absolute h-7 w-px animate-pulse bg-primary" />}
              </div>
            </div>
          )
        })}
      </div>
      <input
        ref={ref}
        id={id}
        value={formatCode(digits)}
        onChange={(e) => onChange(normalizeCode(e.target.value))}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text')
          const parsed = parsePairingInput(text)
          if (parsed.code) {
            e.preventDefault()
            onChange(parsed.code)
            onNonce?.(parsed.nonce)
          }
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        disabled={disabled}
        aria-label="Pairing code"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        spellCheck={false}
        className="absolute inset-0 h-full w-full cursor-text bg-transparent text-transparent caret-transparent opacity-0 outline-none selection:bg-transparent"
      />
    </div>
  )
}
